import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { gsap } from '@/lib/gsap'
import { pad } from '@/lib/format'
import { hasFinePointer } from '@/lib/motion'
import { SplitChars } from '@/components/motion/split-text'
import Img from '@/components/generals/img'
import { shopCategories } from '@/constants/site'
import { productShots } from '@/constants/media'

interface CategoriesProps {
    counts: Record<string, number>
}

const PREVIEWS: Record<string, { src: string, alt: string }> = {
    outerwear: { src: productShots.utilityBomberDetail, alt: "A close portrait in a short black utility jacket" },
    knitwear: { src: productShots.mohairCardiganDetail, alt: "A textured knitted cardigan with a shawl collar and buttoned front" },
    denim: { src: productShots.choreJacketDetail, alt: "A man in a denim jacket carrying a camera, looking up" },
    accessories: { src: productShots.archiveToteDetail, alt: "Leather bag detail with stitching and metal hardware" }
}

/** Oversized category index; a photo follows the cursor and swaps as you move down the list. */
const Categories: FC<CategoriesProps> = ({ counts = {} }): ReactNode => {
    const list = useRef<HTMLUListElement>(null)
    const preview = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const listEl = list.current
        const previewEl = preview.current

        if (!listEl || !previewEl || !hasFinePointer()) return

        const frames = Array.from(previewEl.querySelectorAll<HTMLElement>('[data-preview]'))
        const xTo = gsap.quickTo(previewEl, 'x', { duration: 0.7, ease: 'expo.out' })
        const yTo = gsap.quickTo(previewEl, 'y', { duration: 0.7, ease: 'expo.out' })
        const tilt = gsap.quickTo(previewEl, 'rotate', { duration: 0.9, ease: 'expo.out' })
        let lastX = 0
        let active = -1

        gsap.set(previewEl, { xPercent: -50, yPercent: -50, scale: 0.6, autoAlpha: 0 })

        const onMove = (event: PointerEvent): void => {
            xTo(event.clientX)
            yTo(event.clientY)
            tilt(gsap.utils.clamp(-12, 12, (event.clientX - lastX) * 0.6))
            lastX = event.clientX

            const row = (event.target as Element).closest<HTMLElement>('[data-row]')
            const next = row ? Number(row.dataset.row) : -1

            if (next === active || next < 0) return

            const incoming = frames[next]

            active = next
            gsap.set(incoming, { zIndex: 2 })
            gsap.fromTo(incoming, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.7, ease: 'expo.out' })
            frames.forEach((frame, index) => {
                if (index !== next) gsap.set(frame, { zIndex: 1 })
            })
        }

        const onEnter = (event: PointerEvent): void => {
            gsap.set(previewEl, { x: event.clientX, y: event.clientY })
            gsap.to(previewEl, { scale: 1, autoAlpha: 1, duration: 0.6, ease: 'expo.out' })
        }

        const onLeave = (): void => {
            active = -1
            gsap.to(previewEl, { scale: 0.6, autoAlpha: 0, duration: 0.5, ease: 'expo.out' })
        }

        listEl.addEventListener('pointermove', onMove)
        listEl.addEventListener('pointerenter', onEnter)
        listEl.addEventListener('pointerleave', onLeave)

        return () => {
            listEl.removeEventListener('pointermove', onMove)
            listEl.removeEventListener('pointerenter', onEnter)
            listEl.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <section id='categories' aria-label='Shop by category' className='relative bg-ink py-24 md:py-36'>
            <div className='flex flex-wrap items-end justify-between gap-6 gutter'>
                <h2 data-reveal='chars' className='display-l uppercase'>
                    <SplitChars text='Shop the essentials' />
                </h2>

                <p data-reveal='fade' className='label-mono text-acid'>
                    (06) — Categories
                </p>
            </div>

            <ul ref={list} className='group/list mt-12 border-t border-line md:mt-16'>
                {shopCategories.map((category, index) => {
                    const total = counts[category.slug] ?? 0

                    return (
                        <li key={category.slug} data-row={index} className='border-b border-line'>
                            <Link
                                href={`/shop?category=${category.slug}`}
                                className='group flex items-center justify-between gap-6 py-5 transition-opacity duration-500 gutter md:py-6 group-hover/list:opacity-40 hover:opacity-100!'
                            >
                                <span className='flex items-baseline gap-5 md:gap-10'>
                                    <span className='meta-mono text-ash transition-colors duration-300 group-hover:text-acid'>
                                        {pad(index + 1)}
                                    </span>

                                    <span data-reveal='chars' className='block font-heading text-[clamp(40px,6.4vw,104px)] uppercase leading-[0.88] transition-transform duration-700 ease-expo-out group-hover:translate-x-3 md:group-hover:translate-x-6'>
                                        <SplitChars text={category.label} />
                                    </span>
                                </span>

                                <span className='flex shrink-0 items-center gap-4 label-mono text-ash transition-colors duration-300 group-hover:text-bone'>
                                    <span className='hidden sm:inline'>
                                        {pad(total)} {total === 1 ? 'piece' : 'pieces'}
                                    </span>

                                    <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-500 ease-expo-out group-hover:rotate-45' />
                                </span>
                            </Link>
                        </li>
                    )
                })}
            </ul>

            <div
                ref={preview}
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-30 hidden aspect-[3/4] w-[clamp(190px,17vw,280px)] overflow-hidden opacity-0 lg:block'
            >
                {shopCategories.map(category => (
                    <div key={category.slug} data-preview className='absolute inset-0'>
                        <Img src={PREVIEWS[category.slug]?.src} alt={PREVIEWS[category.slug]?.alt ?? category.label} sizes='280px' />
                    </div>
                ))}
            </div>
        </section>
    )
}

export default Categories
